import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChatMessagePrompt } from '~/components/chat-message-prompt/ChatMessagePrompt';
import { Action } from '~/stores/use-actions/actions.types';
import { useActions } from '~/stores/use-actions/useActions';
import { ActionWatchlistCreate } from '~/stores/use-chat/assistant-response.type';
import { Message, MessageReply } from '~/stores/use-chat/use-chat.types';
import { useChat } from '~/stores/use-chat/useChat';
import { createNewMessage } from '~/stores/use-chat/utils/updateMessage';

import { CompareEntitiesOnChart } from './components/compare-entities-on-chart/CompareEntitiesOnChart';
import { CreateWatchlistFromRelatedCompanies } from './components/create-watchlist-from-related-companies/CreateWatchlistFromRelatedCompanies';
import { CreateWatchlistPromptModal } from './components/create-watchlist-prompt-modal/CreateWatchlistPromptModal';
import * as S from './Prompts.styles';

export interface PromptsProps {
  actions: Action[];
}
const MESSAGE_LOADING_DELAY_MS = 300;

export const Prompts = ({ actions }: PromptsProps) => {
  const { t } = useTranslation('widget');
  const { append, update } = useChat(state => ({
    append: state.append,
    update: state.update,
  }));
  const removeAction = useActions(state => state.removeAction);
  const [showWatchlistModal, setShowWatchlistModal] = useState(false);
  const [activeAction, setActiveAction] = useState<Action>();

  const addMessageWithDelay = (message: Partial<Message>) => {
    const newMessage = createNewMessage(message);
    append(newMessage);

    setTimeout(() => {
      update({
        id: newMessage.id,
        status: 'complete',
        hasSeen: true,
      });
    }, MESSAGE_LOADING_DELAY_MS);
  };

  const onPromptClick = (singleAction: Action, question?: string) => {
    const [service] = singleAction.action.split(':');

    if (singleAction.action === 'watchlist:create') {
      setShowWatchlistModal(true);
      setActiveAction(singleAction);
      return;
    }

    addMessageWithDelay({
      status: 'pending',
      question,
      reply: [
        {
          source: formatMessageSource(service),
          data: singleAction.data,
        } as MessageReply,
      ],
    });
    removeAction(singleAction);
  };

  const formatMessageSource = (service: string) => {
    let formattedService = service;

    // Force backend value to match our allowed reply type
    if (service === 'scenario') {
      formattedService = 'scenario-assistant';
    }

    return formattedService;
  };

  const hasWatchlistCreateActions = !!actions.find(
    a => a.action === 'watchlist:create'
  );

  const renderAction = (singleAction: Action) => {
    switch (singleAction.action) {
      case 'chart-comparison:compare-entities': {
        return <CompareEntitiesOnChart {...singleAction.data} />;
      }

      case 'watchlist:create-related-companies': {
        return <CreateWatchlistFromRelatedCompanies {...singleAction.data} />;
      }

      default: {
        const [service] = singleAction.action.split(':');

        /* i18next-extract-disable-next-line */
        const message = t(singleAction.display);

        return (
          <ChatMessagePrompt
            data-testid={singleAction.display}
            /* i18next-extract-mark-context-next-line ["chat-quality-score", "past-episodes-widget", "scenario", "watchlist"] */
            title={t('widget:chatPrompts.title', {
              context: service,
            })}
            message={<span>{message}</span>}
            onClick={() => onPromptClick(singleAction, message)}
          />
        );
      }
    }
  };

  return (
    <S.PromptsRoot data-testid="prompts">
      {actions.map(action => (
        <Fragment key={action.display}>{renderAction(action)}</Fragment>
      ))}

      <CreateWatchlistPromptModal
        isOpen={showWatchlistModal && hasWatchlistCreateActions}
        closeModal={() => setShowWatchlistModal(false)}
        action={activeAction as ActionWatchlistCreate}
      />
    </S.PromptsRoot>
  );
};
